import React, { useEffect, useState } from 'react';
import api from '../../config/axios';
import { useNavigate, useLocation } from 'react-router-dom';
import '../order/OrderCreate.css';

interface MenuItem {
  _id: string;
  name: string;
  price: number;
  description?: string;
  imageUrl: string;
}

interface OrderItem {
  menuId: string;
  name: string;
  price: number;
  quantity: number;
}

const OrderCreateByQR: React.FC = () => {
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [tableId, setTableId] = useState('');
  const [tableName, setTableName] = useState<string>("");
  const [orders, setOrders] = useState<any[]>([]);
  const [searchPhone, setSearchPhone] = useState('');
  const [filteredOrders, setFilteredOrders] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tid = params.get('tableId');
    if (tid) setTableId(tid);
  }, [location.search]);

  useEffect(() => {
    const fetchTable = async () => {
      if (!tableId) return;
      try {
        const res = await api.get(`/api/tables`);
        const found = (res.data || []).find((t: any) => t._id === tableId);
        
        if (!found) {
          setError('Không tìm thấy bàn này!');
          return;
        }
        
        setTableName(found.name);
        
        if (found.status === 'in-use') {
          setError('Bàn này đang được sử dụng! Vui lòng liên hệ nhân viên.');
          return;
        }
        
        if (found.status === 'reserved') {
          setError('Bàn này đã được đặt trước! Vui lòng liên hệ nhân viên.');
          return;
        }
        
        if (found.status === 'available') {
          setError('');
        }
        
      } catch {
        setError('Không thể kiểm tra thông tin bàn!');
        setTableName(tableId);
      }
    };
    fetchTable();
  }, [tableId]);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const res = await api.get('/api/menu');
        setMenu(res.data.data || res.data);
      } catch {
        setMenu([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, []);

  const fetchOrders = async () => {
    if (!tableId) return;
    try {
      const res = await api.get(`/api/orders/guest/${tableId}`);
      setOrders((res.data.data || []));
    } catch {
      setOrders([]);
    }
  };

  useEffect(() => {
    if (!searchPhone.trim()) {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter(order => 
        order.customerPhone && 
        order.customerPhone.replace(/[\s\-\(\)]/g, '').includes(searchPhone.replace(/[\s\-\(\)]/g, ''))
      );
      setFilteredOrders(filtered);
    }
  }, [orders, searchPhone]);

  useEffect(() => { 
    if (tableId) {
      fetchOrders(); 
    }
  }, [tableId]);
  
  useEffect(() => { 
    if (success) {
      setTimeout(() => {
        fetchOrders();
      }, 500);
    }
  }, [success]);

  const handleAddItem = (item: MenuItem) => {
    setOrderItems(prev => {
      const found = prev.find(i => i.menuId === item._id);
      if (found) {
        return prev.map(i => i.menuId === item._id ? { ...i, quantity: i.quantity + 1 } : i);
      } else {
        return [...prev, { menuId: item._id, name: item.name, price: item.price, quantity: 1, imageUrl: item.imageUrl }];
      }
    });
  };

  const handleChangeQty = (menuId: string, qty: number) => {
    setOrderItems(prev => prev.map(i => i.menuId === menuId ? { ...i, quantity: qty } : i));
  };

  const handleRemoveItem = (menuId: string) => {
    setOrderItems(prev => prev.filter(i => i.menuId !== menuId));
  };

  const subtotal = orderItems.reduce((sum, i) => sum + i.price * i.quantity, 0);

  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    const phoneRegex = /^(0|\+84)(3[2-9]|5[689]|7[06-9]|8[1-689]|9[0-46-9])[0-9]{7}$/;
    return phoneRegex.test(cleanPhone);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomerPhone(value);
    
    if (value.trim() === '') {
      setPhoneError('');
    } else if (!validatePhone(value)) {
      setPhoneError('Số điện thoại không hợp lệ');
    } else {
      setPhoneError('');
    }
  };

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (!customerName.trim() || !customerPhone.trim()) {
      setError('Vui lòng nhập tên và số điện thoại!');
      return;
    }
    if (!validatePhone(customerPhone)) {
      setError('Số điện thoại không hợp lệ! Vui lòng nhập đúng định dạng số điện thoại VN');
      return;
    }
    if (orderItems.length === 0) {
      setError('Vui lòng chọn ít nhất 1 món!');
      return;
    }
    if (!tableId) {
      setError('Không xác định được bàn!');
      return;
    }
    
    try {
      const res = await api.get(`/api/tables`);
      const found = (res.data || []).find((t: any) => t._id === tableId);
      
      if (!found) {
        setError('Không tìm thấy bàn này!');
        return;
      }
      
      if (found.status !== 'available') {
        setError(`Bàn này hiện tại không thể đặt món (Trạng thái: ${found.status === 'in-use' ? 'Đang sử dụng' : 'Đã đặt trước'})! Vui lòng liên hệ nhân viên.`);
        return;
      }
    } catch (err) {
      setError('Không thể kiểm tra trạng thái bàn! Vui lòng thử lại.');
      return;
    }
    try {
      setLoading(true);
      const res = await api.post('/api/orders/guest', {
        tableId,
        customerName,
        customerPhone,
        items: orderItems,
        taxRate: 10,
      });
      setSuccess('Đặt hàng thành công! Đang chuyển đến trang thanh toán...');
      fetchOrders();
      setOrderItems([]);
      setTimeout(() => {
        navigate(`/orders/guest-payment?orderId=${res.data._id || res.data.data?._id}`);
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Lỗi khi tạo đơn hàng');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="order-create-bg">
      <div className="order-create-container">
        <h2 className="order-create-title">ĐẶT MÓN CHO BÀN</h2>
        
        <br />
        <div style={{ marginBottom: 16 }}>
          <b>Bàn:</b> {tableName || tableId}
        </div>
        
        <div style={{ 
          background: '#f0f9ff', 
          border: '1px solid #0ea5e9', 
          borderRadius: 8, 
          padding: 16, 
          marginBottom: 24,
          textAlign: 'center'
        }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#0ea5e9' }}>🍽️ Đặt món nhóm?</h4>
          <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#0369a1' }}>
            Tạo session nhóm để nhiều người cùng đặt món và thanh toán chung
          </p>
          <button 
            onClick={() => navigate(`/orders/group-create?tableId=${tableId}`)}
            className="order-create-menu-add-btn"
            style={{ marginRight: 8 }}
          >
            Tạo nhóm mới
          </button>
          <button 
            onClick={() => navigate('/orders/group-join')}
            className="order-create-menu-add-btn"
          >
            Tham gia nhóm
          </button>
        </div>
        <div className="order-create-inputs-group">
          <input
            type="text"
            placeholder="Tên khách hàng"
            value={customerName}
            onChange={e => setCustomerName(e.target.value)}
            className="order-create-input"
            style={{ marginRight: 8 }}
          />
          <input
            type="tel"
            placeholder="Số điện thoại (0 | +84)"
            value={customerPhone}
            onChange={handlePhoneChange}
            className={`order-create-input ${phoneError ? 'order-create-input-error' : ''}`}
          />
        </div>
        {phoneError && <div className="order-create-message order-create-error" style={{ fontSize: '12px', marginTop: '4px' }}>{phoneError}</div>}
        {error && <div className="order-create-message order-create-error">{error}</div>}
        {success && <div className="order-create-message order-create-success">{success}</div>}
        {loading && <div className="order-create-loading">Đang xử lý...</div>}
        <div className="order-create-main">
          <div className="order-create-left">
            <h3 className="order-create-section-title">Menu</h3>
            <div className="order-create-menu-list">
              {menu.map(item => (
                <div key={item._id} className="order-create-menu-item">
                  <img src={item.imageUrl} alt={item.name} className="order-create-menu-img" />
                  <div className="order-create-menu-info">
                    <div className="order-create-menu-name">{item.name}</div>
                    <div className="order-create-menu-price">{item.price.toLocaleString()} đ</div>
                  </div>
                  <button onClick={() => handleAddItem(item)} className="order-create-menu-add-btn">Thêm</button>
                </div>
              ))}
            </div>
          </div>
          <div className="order-create-right">
            <h3 className="order-create-section-title">Đơn hàng</h3>
            {orderItems.length === 0 ? (
              <div className="order-create-empty">Chưa có món nào.</div>
            ) : (
              <table className="order-create-table">
                <thead>
                  <tr>
                    <th>Món</th>
                    <th>Giá</th>
                    <th>Số lượng</th>
                    <th>Thành tiền</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {orderItems.map(item => (
                    <tr key={item.menuId}>
                      <td>{item.name}</td>
                      <td>{item.price.toLocaleString()} đ</td>
                      <td>
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={e => handleChangeQty(item.menuId, Math.max(1, Number(e.target.value)))}
                          className="order-create-quantity-input"
                        />
                      </td>
                      <td>{(item.price * item.quantity).toLocaleString()} đ</td>
                      <td>
                        <button onClick={() => handleRemoveItem(item.menuId)} className="order-create-remove-btn">X</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ marginTop: 12, fontWeight: 600 }}>Tổng cộng: {subtotal.toLocaleString()} đ</div>
            <button onClick={handleSubmit} className="order-create-submit-btn" disabled={loading} style={{ marginTop: 16 }}>
              {loading ? 'Đang xử lý...' : 'Đặt món'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderCreateByQR; 